import React from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Diff, Button, Tooltip } from "components";
import {
	getFormattedDate,
	getFullName,
	getUserPageUrl,
	permissionList,
	useRequiredPermissions,
} from "helpers";
import { getCurrentUserId } from "store/user/user.reducer";

import NotePhase from "./notePhase";

import { ReactComponent as RemoveIcon } from "static/images/cancel.svg";
import { ReactComponent as ViewIcon } from "static/images/eye.svg";
import { ReactComponent as EditIcon } from "static/images/pencil.svg";
import styles from "./notesTable.module.scss";

const Actions = ({
	onViewClick,
	onEditClick,
	onDeleteClick,
	isCurrentUserNotAuthor,
	//On review. name means nothing. B
	isPermissions,
}) => {
	const canAddNotes = useRequiredPermissions(permissionList.AddAccountNotes);
	const canDeleteNotes = useRequiredPermissions(
		permissionList.DeleteAccountNotes,
	);
	//On review B. I cannot understand what this variable contains.
	const isAddNotesPermissions = isPermissions ? isPermissions : canAddNotes;
	const isDeleteNotesPermissions = isPermissions
		? isPermissions
		: canDeleteNotes;

	return (
		<div className={styles.wrappIcons}>
			<div className={styles.wrappIcons}>
				<Button
					onClick={onViewClick}
					icon={<ViewIcon />}
					btnIcon
					variant="secondary"
					title="View"
				/>
			</div>
			{isAddNotesPermissions && (
				<Button
					onClick={onEditClick}
					icon={<EditIcon />}
					btnIcon
					variant="secondary"
					title="Edit"
					disabled={isCurrentUserNotAuthor}
					className={styles.iconButton}
				/>
			)}
			{isDeleteNotesPermissions && (
				<Button
					onClick={onDeleteClick}
					icon={<RemoveIcon />}
					btnIcon
					variant="secondary"
					title="Delete"
					disabled={isCurrentUserNotAuthor}
					className={styles.iconButton}
				/>
			)}
		</div>
	);
};

export const NoteRow = ({
	note,
	onViewNote,
	onEditNote,
	onDeleteNote,
	isPermissions,
}) => {
	const handleViewClick = () => onViewNote(note);
	const handleEditClick = () => onEditNote(note);
	const handleDeleteClick = () => onDeleteNote(note);

	const currentUserId = useSelector(getCurrentUserId);
	const isCurrentUserNotAuthor = currentUserId !== note.authorId;

	const linkToUser = getUserPageUrl(note?.author);

	const roleTitle = note?.author?.role
		? `${note?.author?.role?.name} - ${note?.author?.agent_office?.name}`
		: null;

	return (
		<div className={styles.bodyTable}>
			<div className={styles.wrappNotesDesc}>
				<div className={clsx(styles.notesHeader, "mb-2")}>
					<div className={styles.date} data-autotest="date">
						{getFormattedDate({
							date: note?.createdAt,
							format: "DD MMMM YYYY",
						})}
					</div>
					<div className={styles.time} data-autotest="time">
						{getFormattedDate({
							date: note?.createdAt,
							format: "h:mm a",
						})}
					</div>
					<div className={styles.company} data-autotest="company">
						{typeof note.author === "object" ? (
							<Tooltip title={roleTitle} disabled={!roleTitle}>
								{linkToUser ? (
									<Link to={linkToUser} target="_blank">
										{getFullName(note?.author)}
									</Link>
								) : (
									getFullName(note?.author)
								)}
							</Tooltip>
						) : (
							note?.author
						)}
					</div>
				</div>
				<div className={styles.mainNotes}>
					<div className={styles.noteBlock}>
						<NotePhase note={note} />
						{!fp.isNil(note?.category) && (
							<div>
								<span className={clsx(styles.desc, "mr-4")}>
									Category
								</span>
								{note.category.displayName}
							</div>
						)}
						{note.title && (
							<div className={styles.title} data-autotest="title">
								<Diff
									newValue={note.title}
									oldValue={note?.lastNoteChange?.oldTitle}
								/>
							</div>
						)}
						<Diff
							data-autotest="note"
							newValue={note.note}
							oldValue={note?.lastNoteChange?.oldNote}
							className={styles.desc}
						/>
					</div>
					<Actions
						isPermissions={isPermissions}
						isCurrentUserNotAuthor={isCurrentUserNotAuthor}
						onViewClick={handleViewClick}
						onEditClick={handleEditClick}
						onDeleteClick={handleDeleteClick}
					/>
				</div>
			</div>
		</div>
	);
};
